import React from 'react';
import { AlertTriangle, Smartphone, ArrowRight } from 'lucide-react';
import { Button } from './button';

interface MobileStoreRedirectProps {
  variant?: 'compact' | 'detailed';
  className?: string;
}

const MobileStoreRedirect: React.FC<MobileStoreRedirectProps> = ({ 
  variant = 'compact',
  className = '' 
}) => {
  if (variant === 'detailed') {
    return (
      <div className={`bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6 ${className}`}>
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
              <Smartphone className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">
              Mobil Cihazdan Satın Alın
            </h3>
            <p className="text-blue-800 dark:text-blue-200 mb-4 leading-relaxed">
              Abonelik satın almak için lütfen mobil cihazınızdan uygulamayı indirin ve satın alma işlemini tamamlayın.
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex items-center gap-3 p-3 bg-white dark:bg-blue-950/40 rounded-lg border border-blue-200 dark:border-blue-800">
                <div className="w-8 h-8 bg-black rounded-lg flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white">
                    <path d="M18.71,19.5c-.83,1.24-1.71,2.45-3.05,2.47-1.34,.03-1.77-.79-3.29-.79-1.53,0-2,.76-3.27,.82-1.31,.05-2.3-1.32-3.14-2.53C4.25,17.48,2.94,13.8,4.7,11.2c.87-1.3,2.17-2.13,3.69-2.15,1.32-.02,2.55,.88,3.36,.88,.8,0,2.3-1.09,3.87-.93,.66,.02,2.5,.26,3.7,1.98-.1,.06-2.2,1.28-2.19,3.81,.02,3.02,2.65,4.03,2.68,4.04-.03,.07-.42,1.44-1.38,2.83Zm-5.94-17.5c-.73,.86-1.94,1.55-3.08,1.38-.15-1.17,.42-2.35,1.15-3.13,.73-.82,1.99-1.45,3.03-1.5,.13,1.24-.33,2.46-1.1,3.25Z"/>
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-blue-900 dark:text-blue-100">iOS App Store</p>
                  <p className="text-sm text-blue-700 dark:text-blue-300">iPhone & iPad</p>
                </div>
                <ArrowRight className="h-4 w-4 text-blue-600 dark:text-blue-400 ml-auto" />
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-white dark:bg-blue-950/40 rounded-lg border border-blue-200 dark:border-blue-800">
                <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white">
                    <path d="M3,20.5V3.5C3,2.91,3.34,2.39,3.84,2.15L13.69,12L3.84,21.85C3.34,21.6,3,21.09,3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08,20.75,11.5,20.75,12C20.75,12.5,20.53,12.9,20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-blue-900 dark:text-blue-100">Google Play Store</p>
                  <p className="text-sm text-blue-700 dark:text-blue-300">Android</p>
                </div>
                <ArrowRight className="h-4 w-4 text-blue-600 dark:text-blue-400 ml-auto" />
              </div>
            </div>
            
            <div className="mt-4 p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <strong>Neden mobil cihaz gerekli?</strong> Apple ve Google'ın politikaları gereği, abonelik satın alma işlemleri yalnızca resmi mağazalar üzerinden gerçekleştirilebilir.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Compact variant
  return (
    <div className={`bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 ${className}`}>
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="font-medium text-amber-900 dark:text-amber-100 mb-1">
            Mobil Cihaz Gerekli
          </p>
          <p className="text-sm text-amber-800 dark:text-amber-200 mb-3">
            Bu paketi satın almak için lütfen mobil cihazınızdan iOS App Store veya Google Play Store'u kullanın.
          </p>
          <div className="flex gap-2 text-xs">
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 rounded">
              <svg viewBox="0 0 24 24" className="w-3 h-3 fill-current">
                <path d="M18.71,19.5c-.83,1.24-1.71,2.45-3.05,2.47-1.34,.03-1.77-.79-3.29-.79-1.53,0-2,.76-3.27,.82-1.31,.05-2.3-1.32-3.14-2.53C4.25,17.48,2.94,13.8,4.7,11.2c.87-1.3,2.17-2.13,3.69-2.15,1.32-.02,2.55,.88,3.36,.88,.8,0,2.3-1.09,3.87-.93,.66,.02,2.5,.26,3.7,1.98-.1,.06-2.2,1.28-2.19,3.81,.02,3.02,2.65,4.03,2.68,4.04-.03,.07-.42,1.44-1.38,2.83Zm-5.94-17.5c-.73,.86-1.94,1.55-3.08,1.38-.15-1.17,.42-2.35,1.15-3.13,.73-.82,1.99-1.45,3.03-1.5,.13,1.24-.33,2.46-1.1,3.25Z"/>
              </svg>
              App Store
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 rounded">
              <svg viewBox="0 0 24 24" className="w-3 h-3 fill-current">
                <path d="M3,20.5V3.5C3,2.91,3.34,2.39,3.84,2.15L13.69,12L3.84,21.85C3.34,21.6,3,21.09,3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08,20.75,11.5,20.75,12C20.75,12.5,20.53,12.9,20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
              </svg>
              Play Store
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileStoreRedirect; 